// frontend/src/components/detail/tables/ProductTable.jsx
import React from 'react';
import { Box, Typography, Chip, Tooltip, IconButton, Link as MuiLink } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { DataTable } from '../../tablekit';
import { slugify } from '../../../utils/slug';
import { F } from '../../../utils/qualFmt';

/* -------------------- width map (edit here) -------------------- */
const W = {
  parent: {
    product: { minWidth: 260 },                 // Product
    count: { width: 120, align: 'right' },      // Interviews
    satisfaction: { minWidth: 240 },            // Satisfaction (qual text)
    // (Quote removed from parent)
    open: { width: 64, align: 'center' }        // Open
  },
  child: {
    name: { width: 160 },                        // Name
    demographics: { minWidth: 260 },             // Demographics
    date: { width: 120 },                        // Date
    satisfaction: { minWidth: 200, align: 'right' }, // Satisfaction (qual text)
    quote: { minWidth: 320 },                    // Quote
    open: { width: 64, align: 'center' }         // Open
  }
};

/* -------------------- helpers -------------------- */

// Flatten segmentation_data into a readable single line
function formatDemographics(seg) {
  if (!seg || typeof seg !== 'object') return '—';
  const entries = Object.entries(seg)
    .map(([k, v]) => {
      if (v == null) return null;
      if (Array.isArray(v)) {
        const list = v.map(x => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ');
        return `${k}: ${list}`;
      }
      if (typeof v === 'object') {
        const inner = Object.entries(v)
          .map(([ik, iv]) => `${ik}: ${Array.isArray(iv) ? iv.join(', ') : String(iv)}`)
          .join('; ');
        return `${k}: { ${inner} }`;
      }
      return `${k}: ${String(v)}`;
    })
    .filter(Boolean);
  return entries.length ? entries.join(' • ') : '—';
}

// Build interview sub-rows from Customer Stories for a given product category & job
function buildInstancesFromCustomerStories({ customerStories, productCategory, jobName }) {
  if (!Array.isArray(customerStories) || !productCategory) return [];
  const targetProduct = slugify(productCategory);
  const targetJob = jobName ? slugify(jobName) : null;

  const matchesJob = (story) => {
    if (!targetJob) return true;
    const arr = Array.isArray(story?.jobs_to_be_done) ? story.jobs_to_be_done : [];
    return arr.some(j => slugify(j?.job_to_be_done_category || '') === targetJob);
  };

  const filtered = customerStories.filter((s) =>
    slugify(s?.solution_choice_category || '') === targetProduct && matchesJob(s)
  );

  return filtered.map((s, i) => {
    const name = s.customer_name || '—';
    const date = s.conversation_date || '—';
    const demographics = formatDemographics(s.segmentation_data);
    const satisfaction =
      s.satisfaction_level ??
      s.solution_satisfaction_level ??
      s.product_satisfaction_level ??
      null;
    const quote =
      s.satisfaction_level_quote ||
      s.solution_satisfaction_quote ||
      s.product_satisfaction_quote ||
      s.quote ||
      '—';
    const href = `/conversations?search=${encodeURIComponent(name)}`;
    return {
      key: `${name}-${date}-${i}`,
      name,
      demographics,
      date,
      satisfaction,
      quote,
      href,
      rawStory: s
    };
  });
}

/** If rowsSource is empty, build parent rows directly from stories. */
function buildParentsFromStories({ customerStories = [], jobName }) {
  // Group by product category (solution_choice_category)
  const groups = new Map(); // slug → { product, instances: [] }
  for (const s of customerStories) {
    const productCategory = s?.solution_choice_category || 'Uncategorized';
    const slug = slugify(productCategory);
    if (!groups.has(slug)) groups.set(slug, { product: productCategory, instances: [] });
    // Reuse instance builder for consistent formatting
    const inst = buildInstancesFromCustomerStories({ customerStories: [s], productCategory, jobName });
    groups.get(slug).instances.push(...inst);
  }

  const rows = [];
  for (const [, data] of groups) {
    const { product, instances } = data;
    const count = instances.length;

    // average satisfaction across instances (0–10, when numeric)
    const satVals = instances
      .map(i => (Number.isFinite(Number(i.satisfaction)) ? Number(i.satisfaction) : null))
      .filter(v => v != null);
    const avgSat = satVals.length ? satVals.reduce((a, b) => a + b, 0) / satVals.length : null;

    rows.push({
      key: slugify(product),
      product,
      count,
      satisfaction: avgSat,
      linkTo: `/product/${slugify(product)}`,
      instances: instances.sort((a, b) => (new Date(b.date) - new Date(a.date)))
    });
  }

  // sort by interviews desc, then product asc
  rows.sort((a, b) => (b.count - a.count) || String(a.product).localeCompare(String(b.product)));
  return rows;
}

/* -------------------- component -------------------- */

export default function ProductsTable({
  rowsSource = [],
  customerStories = [],
  jobName,
  title = 'Products'
}) {
  const location = useLocation();

  // Build the visible rows:
  // 1) use rowsSource if provided (legacy/other pages),
  // 2) otherwise aggregate from stories (this page).
  const rows = React.useMemo(() => {
    if (Array.isArray(rowsSource) && rowsSource.length) {
      return rowsSource.map((t, i) => {
        const title = t.title || t.solution_choice_category || t.product || '—';
        const productCategory = t.solution_choice_category || t.category || title;
        const satisfaction = t.satisfaction_level ?? t.satisfaction ?? t.score ?? null;
        const justification = t.satisfaction_level_justification || t.justification || null;
        const count = t.count ?? t.interviewCount ?? t.frequency ?? 0;
        const slug = slugify(productCategory || title);
        const linkTo = slug ? `/product/${slug}${location.search || ''}` : null;

        const instances = buildInstancesFromCustomerStories({
          customerStories,
          productCategory,
          jobName
        });

        return {
          key: `${slug || `row-${i}`}`,
          product: productCategory || title,
          count,
          satisfaction,
          justification,
          linkTo,
          instances,
          raw: t
        };
      });
    }
    // No rowsSource → derive parents from stories
    return buildParentsFromStories({ customerStories, jobName }).map(r => ({
      ...r,
      linkTo: r.linkTo ? `${r.linkTo}${location.search || ''}` : r.linkTo
    }));
  }, [rowsSource, location.search, customerStories, jobName]);

  if (!rows.length) return null;

  /* ---------- Parent columns (Quote REMOVED) ---------- */
  const columns = [
    {
      key: 'product',
      header: 'Product',
      render: (v, r) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.product}</Typography>
          {r.justification && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {r.justification}
            </Typography>
          )}
        </Box>
      ),
      ...W.parent.product
    },
    { key: 'count', header: 'Interviews', type: 'number', align: 'right', ...W.parent.count },
    {
      key: 'satisfaction',
      header: 'Satisfaction',
      accessor: (r) => (Number.isFinite(Number(r.satisfaction)) ? Number(r.satisfaction) : null),
      render: (v) =>
        v != null ? (
          <Chip size="small" label={F.productSatisfaction(v).text} />
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>—</Typography>
        ),
      ...W.parent.satisfaction
    },
    {
      key: 'linkTo',
      header: 'Open',
      accessor: (r) => r.linkTo || '',
      align: 'center',
      render: (href) =>
        href ? (
          <Tooltip title="Open product details">
            <IconButton size="small" component={RouterLink} to={href} aria-label="Open product details">
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          '—'
        ),
      ...W.parent.open
    }
  ];

  /* ---------- Sub-table columns (instances) ---------- */
  const subTables = [
    {
      key: 'instances',
      title: 'Interviews',
      columns: [
        { key: 'name', header: 'Name', ...W.child.name },
        { key: 'demographics', header: 'Demographics', ...W.child.demographics },
        { key: 'date', header: 'Date', type: 'date', ...W.child.date },
        {
          key: 'satisfaction',
          header: 'Satisfaction',
          type: 'number',
          align: 'right',
          accessor: (r) => (Number.isFinite(Number(r.satisfaction)) ? Number(r.satisfaction) : null),
          render: (v) => (v != null ? F.productSatisfaction(v).text : '—'),
          ...W.child.satisfaction
        },
        {
          key: 'quote',
          header: 'Quote',
          accessor: (r) => r.quote || '—',
          render: (text, row) =>
            text && row?.href ? (
              <MuiLink
                href={row.href}
                underline="hover"
                target="_blank"
                rel="noopener"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
                aria-label="Open quote clip"
              >
                <PlayCircleOutlineIcon fontSize="small" />
                <Typography component="span" variant="body2">{text}</Typography>
              </MuiLink>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>{text || '—'}</Typography>
            ),
          ...W.child.quote
        },
        {
          key: 'href',
          header: 'Open',
          accessor: (r) => r.href || '',
          align: 'center',
          render: (href) =>
            href ? (
              <Tooltip title="Open interview">
                <IconButton
                  size="small"
                  component="a"
                  href={href}
                  target="_blank"
                  rel="noopener"
                  aria-label="Open interview"
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : '—',
          ...W.child.open
        }
      ]
    }
  ];

  return (
    <DataTable
      title={title}
      columns={columns}
      rows={rows}
      subTables={subTables}
      dense
    />
  );
}

